import { createContext, useContext, useState, useEffect } from 'react'
import { useIsAuthenticated } from '@azure/msal-react'
import { forceRefreshToken } from '../services/apiClient'
import { hasImpersonationToken } from '../services/impersonationSession'
import { callContract } from '../contractClient'
import { contracts as meContracts } from '../../../../shared/features/me.contracts'

const PermissionContext = createContext(null)

export function PermissionProvider({ children }) {
  const msalAuthenticated = useIsAuthenticated()
  // Impersonation sessions have NO MSAL account — the admin-minted token in
  // sessionStorage is the sole credential (same predicate as AppRouter).
  // Gating the roles fetch on MSAL alone rendered impersonated sessions
  // permissionless: the worker answers /api/users/me with the session's
  // roles, but it was never asked.
  const isAuthenticated = msalAuthenticated || hasImpersonationToken()
  const [appRoles, setAppRoles] = useState([])
  const [userEmail, setUserEmail] = useState('')
  const [impersonation, setImpersonation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  // Distinguishes "could not resolve permissions" (transient — retry) from a
  // genuine zero-role answer (no access). AccessGate renders them differently;
  // conflating them would misreport outages as revoked access.
  // A 403 IS a genuine answer (the worker refuses role-less sessions), so it
  // counts as resolved-with-zero-roles, not as a load failure.
  const [loadFailed, setLoadFailed] = useState(false)

  const fetchUserPermissions = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true)
        forceRefreshToken() // Tell apiClient to get fresh token from Azure
      }
      const response = await callContract(meContracts.usersMe)
      const user = response.data.user
      setAppRoles(user.appRoles || [])
      setUserEmail(user.email || '')
      setImpersonation(user.impersonation || null)
      setLoadFailed(false)
    } catch (err) {
      console.error('Failed to fetch user permissions:', err)
      setAppRoles([])
      setLoadFailed(err.response?.status !== 403)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserPermissions()
    } else {
      setAppRoles([])
      setUserEmail('')
      setImpersonation(null)
      setLoading(false)
    }
  }, [isAuthenticated])

  // apiClient clears the token and fires this event when the worker reports
  // the impersonation session expired — drop the session's permissions
  // instead of keeping a dead Global.Admin view around.
  useEffect(() => {
    const onEnded = () => {
      setAppRoles([])
      setUserEmail('')
      setImpersonation(null)
    }
    window.addEventListener('impersonation:ended', onEnded)
    return () => window.removeEventListener('impersonation:ended', onEnded)
  }, [])

  // Force refresh permissions from Azure (gets new JWT)
  const refreshPermissions = () => fetchUserPermissions(true)

  // Check if user is Global Admin
  const isGlobalAdmin = () => appRoles.includes('Global.Admin')

  // Check if user has specific permission
  // level: 'Read' | 'Write' | 'Admin'
  const hasPermission = (section, level) => {
    if (isGlobalAdmin()) return true

    // Check exact permission
    if (appRoles.includes(`${section}.${level}`)) return true

    // Admin implies Write and Read
    if (appRoles.includes(`${section}.Admin`)) return true

    // Write implies Read
    if (level === 'Read' && appRoles.includes(`${section}.Write`)) return true

    return false
  }

  // Convenience functions
  const canRead = (section) => hasPermission(section, 'Read')
  const canWrite = (section) => hasPermission(section, 'Write')
  const isAdmin = (section) => hasPermission(section, 'Admin')

  return (
    <PermissionContext.Provider value={{
      appRoles,
      userEmail,
      impersonation,
      loading,
      loadFailed,
      refreshing,
      isGlobalAdmin,
      hasPermission,
      canRead,
      canWrite,
      isAdmin,
      refreshPermissions,
    }}>
      {children}
    </PermissionContext.Provider>
  )
}

export function usePermissions() {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider')
  }
  return context
}
